import { Link, usePage } from "@inertiajs/react";
import {
  CalendarIcon,
  FileTextIcon,
  MapPinIcon,
  ShieldIcon,
  UsersIcon,
} from "lucide-react";

import Layout from "@/components/Layout";
import { OAuthButtons } from "@/components/OAuthButtons";

interface IndexProps {
  oauth_providers: string[];
}

export default function Index({ oauth_providers }: IndexProps) {
  const { user, primary_cfp } = usePage().props;

  const cfp_closed = primary_cfp?.is_closed ?? true;
  const cfp_future = primary_cfp?.is_future ?? false;

  return (
    <Layout currentUser={user}>
      <div className="px-sky-grad-day px-card animate-fade-in relative mb-12 overflow-hidden p-8">
        <img
          src="/px-cloud.png"
          alt=""
          width={34}
          height={14}
          className="px-pixelated px-bob absolute top-6 right-8 w-24"
          aria-hidden="true"
        />
        <img
          src="/logo8bit-mascot.png"
          alt=""
          width={392}
          height={448}
          className="px-pixelated px-bob pointer-events-none absolute top-1/2 right-10 hidden w-32 -translate-y-1/2 [--px-bob-delay:-1.2s] lg:block"
          aria-hidden="true"
        />
        <h1 className="px-font px-shadow-ink mb-5 text-lg text-white md:text-2xl">
          Call for Proposals
        </h1>
        <p className="text-royal-950 max-w-3xl text-lg">
          Share your Ruby knowledge and experience at the{" "}
          <a
            className="text-royal-900 font-medium underline"
            href="https://sfruby.com"
            target="_blank"
            rel="noreferrer"
          >
            San Francisco Ruby Startup Conference 2026
          </a>
          . We’re looking for talks to support and inspire the new generation of
          successful startups built on Ruby and Rails.
        </p>
        <div className="text-royal-950 mt-8 flex flex-wrap gap-4">
          <div className="flex items-center">
            <CalendarIcon className="mr-2 h-5 w-5" />
            <span>November 10-12, 2026</span>
          </div>
          <div className="flex items-center">
            <MapPinIcon className="mr-2 h-5 w-5" />
            <span>SFJAZZ, San Francisco</span>
          </div>
          <div className="flex items-center">
            <UsersIcon className="mr-2 h-5 w-5" />
            <span>450+ Attendees</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
        <div className="animate-slide-up space-y-8">
          <div>
            <h2 className="px-font text-ink mb-5 text-sm md:text-base">
              Proposal Guidelines
            </h2>
            <div className="space-y-6">
              <p>
                Thank you for your interest in speaking at our conference! We’re
                excited to invite proposals for talks in the following tracks:
              </p>
              <ul className="list-disc space-y-2 pl-6">
                <li>
                  <span className="font-medium">Ruby startup stories</span>
                  <p>
                    Have you achieved outsized productivity as a small but
                    ambitious team? Built tooling or open source that empowers
                    the most ambitious new products? Share your productivity and
                    growth story.
                  </p>
                </li>
                <li>
                  <span className="font-medium">Ruby scaleup stories</span>
                  <p>
                    Has your organization reinvented itself in the current AI
                    cycle and reached outstanding results? Have you scaled Ruby,
                    your product, and your team beyond what seemed possible?
                    Share the recipes.
                  </p>
                </li>
              </ul>
              <p>
                Have an idea that doesn’t quite fit either track? Don’t let that
                stop you—we also accept proposals in the General track for
                anything else you’d love to share with the Ruby community.
              </p>
              <p>
                Each talk should be 30 minutes in total, including time for Q&A.
                We’re especially interested in talks that are:
              </p>
              <ul className="list-disc space-y-2 pl-6">
                <li>Educational and informative</li>
                <li>Grounded in real-world experience</li>
                <li>Accessible to Ruby developers of all levels</li>
              </ul>
            </div>
          </div>

          <div>
            <h2 className="px-font text-ink mb-5 text-sm md:text-base">
              Selection Process
            </h2>
            <div className="space-y-4">
              <p>
                All proposals will be thoughtfully reviewed by our program
                committee. Talks will be selected based on the following
                criteria:
              </p>
              <ul className="list-disc space-y-2 pl-6">
                <li>Relevance to the specified tracks</li>
                <li>Originality and freshness of the topic</li>
                <li>Speaker’s experience and expertise</li>
                <li>Overall balance of the conference program</li>
              </ul>
              <p>
                <span className="font-medium">Important dates:</span>
              </p>
              <ul className="list-disc space-y-1 pl-6">
                <li>CFP opens: July 6, 2026</li>
                <li>CFP closes: July 27, 2026</li>
                <li>Notifications: August 1, 2026</li>
              </ul>
            </div>
          </div>

          <div>
            <h2 className="px-font text-ink mb-5 text-sm md:text-base">
              Speaker Benefits
            </h2>
            <div className="space-y-4 text-neutral-700">
              <p>Selected speakers will receive:</p>
              <ul className="list-disc space-y-2 pl-6">
                <li>A complimentary conference ticket</li>
                <li>Invitation to the speaker dinner</li>
              </ul>
              <p>
                To help keep our conference affordable, we kindly ask that you
                check if your employer is able to support your travel. If so,
                we’d be happy to recognize them as a travel sponsor.
              </p>
              <p>We look forward to your proposals!</p>
            </div>
          </div>
        </div>

        <div className="space-y-8 lg:pl-8">
          {user?.is_admin && (
            <div data-test-id="admin-panel" className="card animate-slide-up">
              <h3 className="px-font text-px10 text-ink mb-4">Admin</h3>
              <a
                href="/admin"
                className="btn btn-outline flex items-center justify-center"
              >
                <ShieldIcon className="mr-2 h-5 w-5" />
                Open Admin Panel
              </a>
            </div>
          )}
          <div
            data-test-id="home-actions"
            className="card animate-slide-up"
            style={{ animationDelay: "0.1s" }}
          >
            {cfp_closed && (
              <>
                {cfp_future && (
                  <h3 className="px-font text-px10 text-ink text-center">
                    The call for proposals is not open yet
                  </h3>
                )}
                {!cfp_future && (
                  <h3 className="px-font text-px10 text-ink mb-6">
                    The call for proposals is now closed
                  </h3>
                )}
                {!cfp_future && (
                  <p className="mb-8 text-neutral-600">
                    Thank you everyone for your submissions! You will hear from
                    us shortly!
                  </p>
                )}
              </>
            )}
            {!cfp_closed && (
              <>
                <h3 className="px-font text-px10 text-ink mb-6">
                  Ready to share your expertise?
                </h3>
                <p className="mb-8 text-neutral-600">
                  We welcome proposals from speakers of all experience levels.
                  Whether you’re a seasoned presenter or a first-time speaker,
                  we want to hear from you!
                </p>
              </>
            )}
            <div className="flex flex-col space-y-2">
              {!user &&
                oauth_providers.map((provider: string) => (
                  <div key={provider}>{OAuthButtons[provider]()}</div>
                ))}

              {user && !cfp_closed && (
                <Link
                  href={`/proposals/new`}
                  className="btn btn-gold flex items-center justify-center gap-2"
                >
                  <img
                    src="/px-quest.png"
                    alt=""
                    width={64}
                    height={296}
                    className="px-pixelated h-5 w-auto"
                  />
                  Submit a Proposal
                </Link>
              )}

              {user && !cfp_future && (
                <Link
                  href={`/proposals`}
                  className="btn btn-outline flex items-center justify-center"
                >
                  <FileTextIcon className="mr-2 h-5 w-5" />
                  View My Proposals
                </Link>
              )}
            </div>
          </div>

          <div
            className="card animate-slide-up"
            style={{ animationDelay: "0.2s" }}
          >
            <h3 className="px-font text-px10 text-ink mb-4">Have questions?</h3>
            <p className="mb-6">
              If you have any questions about the CFP process or need help with
              your proposal, feel free to reach out to our team.
            </p>
            <a
              href="mailto:conference@sfruby.com"
              className="text-primary-600 hover:text-primary-800 inline-flex items-center font-medium transition-colors"
            >
              Contact the CFP team
              <span className="ml-1">→</span>
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
}
